import { ReactElement, useState } from 'react';
import { GetOverviewSummary } from '../types';
import { useTranslation } from 'react-i18next';
import {
  UnitTooltipContainer,
  UnitsTooltipTitle,
  UnitsTooltipSectionTitle,
  UnitsTooltipTable,
  UnitsTooltipHeaderRow,
  UnitsTooltipRow,
} from './styles';
import { BigNumberCard } from './components/Card';
import { useHistory } from 'react-router-dom';
import { handleFilterStateRoute } from '../helpers/filters';
import { useFilterContext } from '../contexts/FilterContext';
import { getUserProfile } from 'helpers/userProfile';
import { normalizeNumber } from '../helpers/numbers';

type UnitsData = {
  units: GetOverviewSummary['UNITS'];
  unitsBySolution: GetOverviewSummary['UNITS_BY_SOLUTION'];
} | undefined;

interface UnitsProps {
  data: UnitsData;
  loading: boolean;
}

type UnitsTooltipProps = Pick<UnitsProps, 'data'>;

export function Units({
  data,
  loading,
}: Readonly<UnitsProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const history = useHistory();
  const { t } = useTranslation();
  const { selectedOptions, clients } = useFilterContext();

  function onClickUnitsDetails(): void {
    history.push('/analise/unidades',
      handleFilterStateRoute(clients, selectedOptions, profile));
  }

  return (
    <BigNumberCard
      id="unit-count"
      icon="Building3Icon"
      number={String(normalizeNumber(data?.units.TOTAL))}
      description={{
        text: t('unidadesSobGestao'),
        tooltip: <UnitsTooltip data={data} />,
      }}
      subDescription={{
        number: normalizeNumber(data?.units.OPERATIONAL),
        text: t('unidadesOperacao'),
      }}
      onClickDetails={onClickUnitsDetails}
      loading={loading}
      disabledTooltip={loading || !data}
      dataTestId="big-number-card-unit"
    />
  );
}

function UnitsTooltip({
  data,
}: Readonly<UnitsTooltipProps>): ReactElement {
  const { t } = useTranslation();

  const serviceRows = [
    { label: t('automacao'), value: data?.unitsBySolution.AUTOMATION ?? 0 },
    { label: t('manutencao'), value: data?.unitsBySolution.HEALTH ?? 0 },
    { label: t('energia'), value: data?.unitsBySolution.ENERGY ?? 0 },
    { label: t('agua'), value: data?.unitsBySolution.WATER ?? 0 },
  ].sort((a, b) => b.value - a.value);

  return (
    <UnitTooltipContainer>
      <UnitsTooltipTitle>
        {`${t('unidades')} ${t('monitoradas')}: ${normalizeNumber(data?.units.MONITORED)}`}
      </UnitsTooltipTitle>
      <div>
        <UnitsTooltipSectionTitle>{t('statusDasUnidades')}</UnitsTooltipSectionTitle>
        <UnitsTooltipTable>
          <thead>
            <tr>
              <UnitsTooltipHeaderRow>{t('status')}</UnitsTooltipHeaderRow>
              <UnitsTooltipHeaderRow>{t('quantidade')}</UnitsTooltipHeaderRow>
              <UnitsTooltipHeaderRow>{t('observacao')}</UnitsTooltipHeaderRow>
            </tr>
          </thead>
          <tbody>
            <tr>
              <UnitsTooltipRow>{t('emOperacao')}</UnitsTooltipRow>
              <UnitsTooltipRow>{normalizeNumber(data?.units.OPERATIONAL)}</UnitsTooltipRow>
              <UnitsTooltipRow $small>{t('unidadesOperandoCorretamente')}</UnitsTooltipRow>
            </tr>
            <tr>
              <UnitsTooltipRow>{t('foraDeOperacao')}</UnitsTooltipRow>
              <UnitsTooltipRow>{normalizeNumber(data?.units.INACTIVE)}</UnitsTooltipRow>
              <UnitsTooltipRow $small>{t('unidadesEmReformaOuEmDesinstalaçãoForcadaDosIoT')}</UnitsTooltipRow>
            </tr>
            {data?.units.INSTALLATION !== undefined && (
              <tr>
                <UnitsTooltipRow>{t('emInstalacao')}</UnitsTooltipRow>
                <UnitsTooltipRow>{normalizeNumber(data.units.INSTALLATION)}</UnitsTooltipRow>
                <UnitsTooltipRow />
              </tr>
            )}
          </tbody>
        </UnitsTooltipTable>
      </div>
      <div>
        <UnitsTooltipSectionTitle>{t('tiposDeServico')}</UnitsTooltipSectionTitle>
        <UnitsTooltipTable>
          <thead>
            <tr>
              <UnitsTooltipHeaderRow>{t('servico')}</UnitsTooltipHeaderRow>
              <UnitsTooltipHeaderRow>{t('quantidade')}</UnitsTooltipHeaderRow>
            </tr>
          </thead>
          <tbody>
            {serviceRows.map((row) => (
              <tr key={row.label}>
                <UnitsTooltipRow>{row.label}</UnitsTooltipRow>
                <UnitsTooltipRow>{normalizeNumber(row.value)}</UnitsTooltipRow>
              </tr>
            ))}
          </tbody>
        </UnitsTooltipTable>
      </div>
    </UnitTooltipContainer>
  );
}
